import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Call } from '@gem/call/CallStore/Call';
import { ICallPersonalizedPermissions } from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps, popupsTasks } from '@gem/popups';

import { ICallMemberPermissionsPopupProps } from './CallMemberPermissionsPopup.types';

interface ICallMemberPermissionsPopupPresenter {
  permissions: ICallPersonalizedPermissions | null;
  channelId: string | null;
  isNewMemberPermissions: boolean;
  ongoingCall: Call;
  onAddClick(): Promise<void>;
  onDeleteButtonClick(): Promise<void>;
  goBack(): void;
  onClose(): void;
}

export const useCallMemberPermissionsPopupPresenter = (
  props: ICommonPopupsProps<ICallMemberPermissionsPopupProps>
): ICallMemberPermissionsPopupPresenter => {
  const { call, callController, userId, handleBackToGeneral } = props.params!;
  const { t } = useTranslation();

  const presenter = useLocalObservable<ICallMemberPermissionsPopupPresenter>(() => ({
    onClose(): void {
      props.popupInstance.close();
      props.onClose?.();
    },

    goBack(): void {
      props.popupInstance.close();
      props.onBack?.();
    },

    async onDeleteButtonClick(): Promise<void> {
      const confirmResult = await popupsTasks.showConfirm({
        text: t('call_permissions_confirm_removal'),
        primaryText: t('common_yes'),
        secondaryText: t('common_no'),
      });

      if (!confirmResult) {
        return;
      }

      if (presenter.channelId) {
        await callController.clearCallPermissionsForUser(presenter.channelId, userId);
      }
      props.popupInstance.close();
      handleBackToGeneral();
    },

    async onAddClick(): Promise<void> {
      if (presenter.channelId && presenter.permissions) {
        callController.setCallPermissionsForUser(
          presenter.channelId,
          userId,
          presenter.permissions
        );
      }
      props.popupInstance.close();
      handleBackToGeneral();
    },

    get ongoingCall(): Call {
      return call;
    },

    get isNewMemberPermissions(): boolean {
      return !presenter.ongoingCall?.permissions.customPermissions.has(userId);
    },

    get channelId(): string | null {
      return presenter.ongoingCall.channelId || null;
    },

    get permissions(): ICallPersonalizedPermissions | null {
      return presenter.ongoingCall.permissions?.editedPermissions?.permissions || null;
    },
  }));

  useEffect(() => {
    if (!presenter.ongoingCall.isStarted) {
      presenter.onClose();
    }
  }, [presenter.ongoingCall.isStarted]);

  return presenter;
};
